import type { FindOperationsInput } from '@travel-planner/shared/schemas/operations';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '@/shared/api/trpc';

const SIXTY_SECONDS = 60 * 1000;

// `keepPrevious` keeps the prior page visible while a new filter/page loads
// (no skeleton flash). Default on for /operations — the list is the primary
// surface and filter switches feel snappier with placeholder data.
export const useOperationItemsData = (
  input: FindOperationsInput,
  {
    keepPrevious = true,
    enabled = true,
  }: { keepPrevious?: boolean; enabled?: boolean } = {}
) =>
  trpc.operations.list.useQuery(input, {
    enabled,
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
    // The list reflects edits made elsewhere — most notably the planner — which
    // never touch the operations query cache (they invalidate only itinerary
    // keys, and the editor is usually a different user/session anyway). Without
    // a refetch trigger, an open Operations tab keeps serving stale data, so
    // items added/edited in the planner appear only after a manual refresh —
    // the "delay between information added vs displayed" (XT-259). Keep it fresh
    // the same way the flagged/failed-message counts do: refetch on entry, when
    // the tab regains focus, and on a 60s poll while open. `refetchInterval`
    // pauses when the tab is hidden (no background polling), so idle tabs cost
    // nothing.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: SIXTY_SECONDS,
    staleTime: SIXTY_SECONDS,
  });
